import "crypto-js/lib-typedarrays";

import { zodResolver } from "@hookform/resolvers/zod";
import { CognitoUser, CognitoUserPool } from "amazon-cognito-identity-js";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

import poolData from "../../../../userpool";
import ErrorToast from "../../../components/ErrorToast";
import PasswordInput from "../../../components/PasswordInput";
import Spinner from "../../../components/Spinner";
import { Button } from "../../../components/ui/button";
import { Field, FieldError, FieldLabel } from "../../../components/ui/field";
import { Input } from "../../../components/ui/input";
import { isValidPassword } from "../../../handlers/validationFunctions";
import AuthLayout from "../layout/AuthLayout";

const userPool = new CognitoUserPool(poolData);

const EASE = [0.22, 1, 0.36, 1] as [number, number, number, number];

const INVALID_PASSWORD_MESSAGE =
	"Use at least 8 characters, with an uppercase letter, a number and a special character.";

const emailSchema = z.object({
	email: z.string().min(1, "Please enter your email address").email("Please enter a valid email address"),
});

const resetSchema = z
	.object({
		code: z.string().min(1, "Please enter the code we emailed you"),
		password: z.string().min(1, "Please choose a new password").refine(isValidPassword, INVALID_PASSWORD_MESSAGE),
		confirmPassword: z.string().min(1, "Please confirm your new password"),
	})
	.refine((values) => values.password === values.confirmPassword, {
		message: "Passwords don't match",
		path: ["confirmPassword"],
	});

type EmailFormValues = z.infer<typeof emailSchema>;
type ResetFormValues = z.infer<typeof resetSchema>;

function ForgotPasswordScreen() {
	const navigate = useNavigate();
	const reduceMotion = useReducedMotion();
	const [step, setStep] = useState<"email" | "reset">("email");
	const [sentTo, setSentTo] = useState("");
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	const cognitoUserRef = useRef<CognitoUser | null>(null);

	const emailForm = useForm<EmailFormValues>({
		resolver: zodResolver(emailSchema),
		defaultValues: { email: "" },
	});

	const resetForm = useForm<ResetFormValues>({
		resolver: zodResolver(resetSchema),
		defaultValues: { code: "", password: "", confirmPassword: "" },
	});

	function requestCode({ email }: EmailFormValues) {
		setError("");
		setLoading(true);

		const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
		cognitoUserRef.current = cognitoUser;

		cognitoUser.forgotPassword({
			onSuccess: function () {
				setLoading(false);
			},
			inputVerificationCode: function () {
				setLoading(false);
				setSentTo(email);
				setStep("reset");
				toast.success("We've emailed you a reset code");
			},
			onFailure: function (err) {
				setLoading(false);
				const code = (err as { code?: string }).code || err?.name;

				if (code === "UserNotFoundException") {
					setError("We couldn't find an account with that email address.");
				} else if (code === "LimitExceededException") {
					setError("Too many attempts. Please wait a few minutes and try again.");
				} else if (code === "InvalidParameterException") {
					setError("That account has no verified email address, so we can't send a reset code.");
				} else {
					setError(err?.message || "We couldn't send a reset code. Please try again.");
				}
			},
		});
	}

	function resetPassword({ code, password }: ResetFormValues) {
		const cognitoUser = cognitoUserRef.current;
		if (!cognitoUser) {
			setError("Your reset session expired. Please request a new code.");
			setStep("email");
			return;
		}

		setError("");
		setLoading(true);

		cognitoUser.confirmPassword(code, password, {
			onSuccess: function () {
				setLoading(false);
				toast.success("Password updated. Please sign in.");
				navigate("/signin");
			},
			onFailure: function (err) {
				setLoading(false);
				const failureCode = (err as { code?: string }).code || err?.name;

				if (failureCode === "CodeMismatchException") {
					resetForm.setError("code", { message: "That code isn't right. Check the email and try again." });
				} else if (failureCode === "ExpiredCodeException") {
					resetForm.setError("code", { message: "That code has expired. Request a new one." });
				} else if (failureCode === "InvalidPasswordException") {
					resetForm.setError("password", { message: INVALID_PASSWORD_MESSAGE });
				} else if (failureCode === "LimitExceededException") {
					setError("Too many attempts. Please wait a few minutes and try again.");
				} else {
					setError(err?.message || "We couldn't reset your password. Please try again.");
				}
			},
		});
	}

	const stepTransition = reduceMotion ? { duration: 0 } : { duration: 0.35, ease: EASE };

	return (
		<AuthLayout>
			<div className="py-12">
				<div className="mb-6">
					<h3 className="font-bold! text-[28px] leading-tight text-[#161616] font-plus-jakarta! mb-2">
						{step === "email" ? "Reset your password" : "Choose a new password"}
					</h3>
					<p className="text-[#5C5C5C] text-sm">
						{step === "email"
							? "Enter the email address you signed up with and we'll send you a reset code."
							: `Enter the code we sent to ${sentTo} and pick a new password.`}
					</p>
				</div>

				{error !== "" && <ErrorToast error={error} />}

				<AnimatePresence initial={false} mode="wait">
					{step === "email" ? (
						<motion.div
							key="email-step"
							initial={reduceMotion ? false : { opacity: 0, x: -8 }}
							animate={{ opacity: 1, x: 0 }}
							exit={reduceMotion ? { opacity: 0 } : { opacity: 0, x: -8 }}
							transition={stepTransition}
						>
							<form noValidate onSubmit={emailForm.handleSubmit(requestCode)} className="tr-form">
								<Field className="gap-1 mb-4" data-invalid={!!emailForm.formState.errors.email}>
									<FieldLabel htmlFor="forgot-password-email">Email</FieldLabel>
									<Input
										id="forgot-password-email"
										type="email"
										placeholder="Enter your email address"
										aria-invalid={!!emailForm.formState.errors.email}
										aria-describedby={emailForm.formState.errors.email ? "forgot-password-email-error" : undefined}
										{...emailForm.register("email")}
									/>
									<FieldError
										id="forgot-password-email-error"
										className="text-error-default"
										errors={[emailForm.formState.errors.email]}
									/>
								</Field>
								<Button type="submit" className="w-full" disabled={loading}>
									{loading ? <Spinner /> : "Send reset code"}
								</Button>
							</form>
						</motion.div>
					) : (
						<motion.div
							key="reset-step"
							initial={reduceMotion ? false : { opacity: 0, x: 8 }}
							animate={{ opacity: 1, x: 0 }}
							exit={reduceMotion ? { opacity: 0 } : { opacity: 0, x: 8 }}
							transition={stepTransition}
						>
							<form noValidate onSubmit={resetForm.handleSubmit(resetPassword)} className="tr-form">
								<Field className="gap-1 mb-4" data-invalid={!!resetForm.formState.errors.code}>
									<FieldLabel htmlFor="forgot-password-code">Reset code</FieldLabel>
									<Input
										id="forgot-password-code"
										type="text"
										inputMode="numeric"
										autoComplete="one-time-code"
										placeholder="Enter the code from your email"
										aria-invalid={!!resetForm.formState.errors.code}
										aria-describedby={resetForm.formState.errors.code ? "forgot-password-code-error" : undefined}
										{...resetForm.register("code")}
									/>
									<FieldError
										id="forgot-password-code-error"
										className="text-error-default"
										errors={[resetForm.formState.errors.code]}
									/>
								</Field>

								<Field className="gap-1 mb-4" data-invalid={!!resetForm.formState.errors.password}>
									<FieldLabel htmlFor="forgot-password-new">New password</FieldLabel>
									<PasswordInput
										id="forgot-password-new"
										placeholder="Create a new password"
										autoComplete="new-password"
										aria-invalid={!!resetForm.formState.errors.password}
										aria-describedby={resetForm.formState.errors.password ? "forgot-password-new-error" : undefined}
										{...resetForm.register("password")}
									/>
									<FieldError
										id="forgot-password-new-error"
										className="text-error-default"
										errors={[resetForm.formState.errors.password]}
									/>
								</Field>

								<Field className="gap-1 mb-4" data-invalid={!!resetForm.formState.errors.confirmPassword}>
									<FieldLabel htmlFor="forgot-password-confirm">Confirm new password</FieldLabel>
									<PasswordInput
										id="forgot-password-confirm"
										placeholder="Confirm your new password"
										autoComplete="new-password"
										aria-invalid={!!resetForm.formState.errors.confirmPassword}
										aria-describedby={
											resetForm.formState.errors.confirmPassword ? "forgot-password-confirm-error" : undefined
										}
										{...resetForm.register("confirmPassword")}
									/>
									<FieldError
										id="forgot-password-confirm-error"
										className="text-error-default"
										errors={[resetForm.formState.errors.confirmPassword]}
									/>
								</Field>

								<Button type="submit" className="w-full" disabled={loading}>
									{loading ? <Spinner /> : "Reset password"}
								</Button>

								<Button
									type="button"
									variant="ghost"
									className="w-full mt-2"
									disabled={loading}
									onClick={() => {
										setError("");
										resetForm.reset();
										setStep("email");
									}}
								>
									Use a different email
								</Button>
							</form>
						</motion.div>
					)}
				</AnimatePresence>

				<div className="text-sm text-[#5C5C5C] mt-4">
					<span>
						Remembered it?{" "}
						<Link to="/signin" className="font-semibold text-primary-700">
							Back to sign in
						</Link>
					</span>
				</div>
			</div>
		</AuthLayout>
	);
}

export default ForgotPasswordScreen;
